/**
 * PostTypeSelector Component
 * Advanced post type selection with preview, validation, and auto-detection
 * 
 * @version 1.0.0
 * @author JGS Tech Team
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import classNames from 'classnames';
import { PostType, PostTypeConfig } from '../../types/postTypes';
import { usePostTypes, usePostTypeValidation } from '../../hooks/usePostTypes';
import Select from '../bootstrap/forms/Select';
import Option from '../bootstrap/Option';
import Button from '../bootstrap/Button';
import Badge from '../bootstrap/Badge';
import Card, { CardBody, CardHeader } from '../bootstrap/Card';
import Icon from '../icon/Icon';
import Alert from '../bootstrap/Alert';
import Progress from '../bootstrap/Progress';

export interface PostTypeSelectorProps {
  selectedPostType?: PostType;
  onPostTypeChange: (postType: PostType) => void;
  disabled?: boolean;
  required?: boolean;
  showPreview?: boolean;
  showAutoDetect?: boolean;
  autoDetectData?: {
    title: string;
    description: string;
    body: string;
    files: File[];
    subArticleCount: number;
  };
  className?: string;
  size?: 'sm' | 'lg';
  validationTrigger?: any; // Trigger validation when this changes
  onValidationChange?: (isValid: boolean, errors: string[]) => void;
}

const PostTypeSelector: React.FC<PostTypeSelectorProps> = ({
  selectedPostType,
  onPostTypeChange,
  disabled = false,
  required = false,
  showPreview = true,
  showAutoDetect = false,
  autoDetectData,
  className,
  size,
  validationTrigger,
  onValidationChange
}) => {
  const { postTypes, postTypeList, isLoading, error, getDisplayInfo } = usePostTypes();
  const { detectPostType, getFileExtensionRules, supportsMultipleSubArticles } = usePostTypeValidation();
  
  const [showDetails, setShowDetails] = useState(false);
  const [autoDetectedType, setAutoDetectedType] = useState<PostType | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Debug selectedPostType
  // useEffect(() => {
  //   console.log('PostTypeSelector - selectedPostType prop:', selectedPostType, 'type:', typeof selectedPostType);
  // }, [selectedPostType]);

  // Auto-detect post type when data changes
  useEffect(() => {
    if (showAutoDetect && autoDetectData) {
      const detected = detectPostType(
        autoDetectData.title,
        autoDetectData.description,
        autoDetectData.body,
        autoDetectData.files,
        autoDetectData.subArticleCount
      );
      setAutoDetectedType(detected);
    }
  }, [showAutoDetect, autoDetectData, detectPostType]);

  // Validate selection
  useEffect(() => {
    const errors: string[] = [];
    
    if (required && !selectedPostType) {
      errors.push('Post type selection is required');
    }

    setValidationErrors(errors);
    onValidationChange?.(errors.length === 0, errors);
  }, [selectedPostType, required, validationTrigger, onValidationChange]);

  // Handle post type selection
  const handlePostTypeChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    const postType = value as PostType;
    
    // Always call onPostTypeChange with the selected value
    onPostTypeChange(postType);
  }, [onPostTypeChange]);

  // Handle auto-detect suggestion
  const handleAutoDetectApply = useCallback(() => {
    if (autoDetectedType) {
      onPostTypeChange(autoDetectedType);
    }
  }, [autoDetectedType, onPostTypeChange]);

  // Get post type preview information
  const selectedConfig = useMemo(() => {
    return selectedPostType ? postTypes[selectedPostType] : null;
  }, [selectedPostType, postTypes]);

  const selectedDisplayInfo = useMemo(() => {
    return selectedPostType ? getDisplayInfo(selectedPostType) : null;
  }, [selectedPostType, getDisplayInfo]);

  const fileRules = useMemo(() => {
    return selectedPostType ? getFileExtensionRules(selectedPostType) : null;
  }, [selectedPostType, getFileExtensionRules]);

  const canHaveSubArticles = useMemo(() => {
    return selectedPostType ? supportsMultipleSubArticles(selectedPostType) : true;
  }, [selectedPostType, supportsMultipleSubArticles]);

  // Format file size for display
  const formatFileSize = (bytes: number): string => {
    if (bytes >= 1024 * 1024) {
      return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
    }
    return `${(bytes / 1024).toFixed(1)}KB`;
  };

  if (error) {
    return (
      <Alert color="danger" isLight>
        <Icon icon="Warning" className="me-2" />
        Failed to load post types: {error}
      </Alert>
    );
  }

  return (
    <div className={classNames('post-type-selector', className)}>
      {/* Auto-detect suggestion */}
      {showAutoDetect && autoDetectedType && autoDetectedType !== selectedPostType && (
        <Alert color="info" isLight className="mb-3">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <Icon icon="AutoAwesome" className="me-2" />
              <span>
                Suggested post type: <strong>{getDisplayInfo(autoDetectedType)?.name}</strong>
              </span>
            </div>
            <Button
              color="info"
              size="sm"
              onClick={handleAutoDetectApply}
              isDisable={disabled}
            >
              Apply
            </Button>
          </div>
        </Alert>
      )}

      {/* Main selector */}
      <div className="mb-3">
        <Select
          value={selectedPostType || ''}
          onChange={handlePostTypeChange}
          disabled={disabled || isLoading}
          required={required}
          size={size}
          ariaLabel="Select Post Type"
          placeholder={isLoading ? 'Loading post types...' : 'Select Post Type'}
          className={classNames({
            'is-invalid': validationErrors.length > 0
          })}
        >
          {isLoading ? (
            <Option value="" disabled>
              Loading post types...
            </Option>
          ) : (
            <>
              <Option value="">Select Post Type</Option>
              {postTypeList
                .filter((config) => !config.hidden) // Filter out hidden/deprecated post types
                .map((config) => {
                const displayText = config.displayName || config.typeName.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
                return (
                  <Option key={config.typeName} value={config.typeName}>
                    {displayText}
                  </Option>
                );
              })}
            </>
          )}
        </Select>

        {/* Validation errors */}
        {validationErrors.length > 0 && (
          <div className="invalid-feedback d-block">
            {validationErrors.join('. ')}
          </div>
        )}
      </div>

      {/* Post type preview */}
      {showPreview && selectedConfig && selectedDisplayInfo && (
        <Card className="mb-3" shadow="sm">
          <CardHeader className="bg-light">
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                {selectedDisplayInfo.icon && (
                  <Icon icon={selectedDisplayInfo.icon} className="me-2 text-primary" />
                )}
                <strong>{selectedDisplayInfo.name}</strong>
                <Badge
                  color={selectedConfig.requiresSpecializedStorage ? 'warning' : 'success'}
                  isLight
                  className="ms-2"
                >
                  {selectedConfig.requiresSpecializedStorage ? 'Specialized' : 'Standard'}
                </Badge>
              </div>
              <Button
                color="light"
                size="sm"
                onClick={() => setShowDetails(!showDetails)}
                icon={showDetails ? 'ExpandLess' : 'ExpandMore'}
              />
            </div>
          </CardHeader>
          <CardBody>
            <p className="text-muted mb-2">{selectedDisplayInfo.description}</p>
            
            {showDetails && (
              <div className="row">
                <div className="col-md-6">
                  <h6 className="mb-2">
                    <Icon icon="FileUpload" className="me-1" />
                    File Requirements
                  </h6>
                  {fileRules && (
                    <ul className="list-unstyled small">
                      <li>
                        <strong>Extensions:</strong> {fileRules.allowed.join(', ')}
                      </li>
                      <li>
                        <strong>Max Size:</strong> {formatFileSize(fileRules.maxSize)}
                      </li>
                      <li>
                        <strong>Type:</strong> {selectedConfig.primaryFileType}
                      </li>
                    </ul>
                  )}
                </div>
                <div className="col-md-6">
                  <h6 className="mb-2">
                    <Icon icon="Settings" className="me-1" />
                    Features
                  </h6>
                  <ul className="list-unstyled small">
                    <li>
                      <Icon 
                        icon={canHaveSubArticles ? 'CheckCircle' : 'Cancel'} 
                        color={canHaveSubArticles ? 'success' : 'danger'} 
                        className="me-1" 
                      />
                      Multiple Sub-articles: {canHaveSubArticles ? 'Yes' : 'No'}
                    </li>
                    <li>
                      <Icon 
                        icon={selectedConfig.requiresSpecializedStorage ? 'Storage' : 'Folder'} 
                        className="me-1" 
                      />
                      Storage: {selectedConfig.requiresSpecializedStorage ? 'Specialized' : 'Standard'}
                    </li>
                    {selectedConfig.validationRules?.minSubArticles && (
                      <li>
                        <Icon icon="Numbers" className="me-1" />
                        Min Sub-articles: {selectedConfig.validationRules.minSubArticles}
                      </li>
                    )}
                    {selectedConfig.validationRules?.maxSubArticles && (
                      <li>
                        <Icon icon="Numbers" className="me-1" />
                        Max Sub-articles: {selectedConfig.validationRules.maxSubArticles}
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {/* Loading indicator */}
      {isLoading && (
        <div className="d-flex align-items-center text-muted">
          <div className="spinner-border spinner-border-sm me-2" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          Loading post type configurations...
        </div>
      )}
    </div>
  );
};

export default PostTypeSelector;
